import React, { useState, useEffect } from 'react';
import Keyword from 'data/Keyword';
import Brand, * as Br from 'data/Brand';
import Vendor from 'data/Vendor';
import { useAuth } from 'lib/Auth';
import { validateBrandDescription, validateBrandSEODescription, validateBrandSEOTitle, validateBrandTitle } from 'utils/brand';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import BrandSEOConfig from './BrandSEOConfig';
import BrandDetailsConfig from './BrandDetailsConfig';
import BrandTypeSelection from './BrandTypeSelection';
import FeatureFlag from 'data/FeatureFlag';
import { useReadPermission } from 'hooks/useUserRoles';
import { PermissionCode } from 'data/UserPermissions';

export interface Props {
  brand: Brand;
  selectedVendor: Vendor;
  vendorsList?: Vendor[];
  keywordsList: Keyword[];
  updateVendor: (name: string, brandId?: number) => void;
  setBrand: (brand: Brand) => void;
  brandsList?: Brand[];
  errors: any;
  setErrors: any;
  hasEditRights?: boolean;
  featureFlags: FeatureFlag;
};

const BrandConfigurationTabV2 = ({ brand, selectedVendor, vendorsList, keywordsList, updateVendor, setBrand, brandsList = [], errors, setErrors, hasEditRights, featureFlags }: Props) => {
  const [keywordsTags, setKeywordsTags] = useState<string[]>([]);
  const { user } = useAuth();

  useEffect(() => {
    if (selectedVendor) {
      setBrand({ ...brand, vendor: selectedVendor });
    }
  }, [selectedVendor]);

  useEffect(() => {
    if (brand) {
      setKeywordsTags(brand.keywords);
    }
  }, [brand && brand.keywords]);

  useEffect(() => {
    if (vendorsList && vendorsList.length > 0 && brand && brand.vendor) {
      const brandVendor = Object.values(brand!.vendor!)[0];
      const currentVendor = vendorsList.find(vendor => vendor.id === brandVendor.id) || selectedVendor;
      updateVendor(currentVendor.name, brand.id);
    }
  }, [vendorsList, brand]);


  const handleBlur = (field: string, value: string, validateFn: Function) => {
    const _errors = validateFn(value);
    setErrors((prevErrors: object) => ({
      ...prevErrors,
      [field]: _errors[field] || '', // Remove the error if validation passes
    }));

    setBrand({ ...brand, [field]: value });
  };

  const onBrandTitleBlur = (title: string) => handleBlur('title', title, validateBrandTitle);
  const onBrandDescBlur = (description: string) => handleBlur('description', description, validateBrandDescription);
  const onSeoTitleBlur = (seoTitle: string) => handleBlur('seoTitle', seoTitle, validateBrandSEOTitle);
  const onSeoDescBlur = (seoDescription: string) => handleBlur('seoDescription', seoDescription, validateBrandSEODescription);

  const content = (
    <FlexboxContainerComponent flexDirection="column">

      {/* Brand type selection */}
      
      <BrandTypeSelection brand={brand} setBrand={setBrand} user={user} brandsList={brandsList} hasEditRights={hasEditRights} />
      
      {/* Brand details configuration */}
      <BrandDetailsConfig
        brand={brand}
        selectedVendor={selectedVendor}
        vendorsList={vendorsList}
        updateVendor={updateVendor}
        setBrand={setBrand}
        errors={errors}
        setErrors={setErrors}
        hasEditRights={hasEditRights}
        onBrandTitleBlur={onBrandTitleBlur}
        onBrandDescBlur={onBrandDescBlur}
        featureFlags={featureFlags}
      />

      {/* Brand SEO configuration */}
      <BrandSEOConfig
        brand={brand}
        setBrand={setBrand}
        errors={errors}
        setErrors={setErrors}
        hasEditRights={hasEditRights}
        onSeoTitleBlur={onSeoTitleBlur}
        onSeoDescBlur={onSeoDescBlur}
        keywordsTags={keywordsTags}
        setKeywordsTags={setKeywordsTags}
        keywordsList={keywordsList}
      />
    </FlexboxContainerComponent>
  );


  return (
    <div>
      {content}
    </div>
  );
};

export default React.memo(BrandConfigurationTabV2);
